// Filter chip bar for the Variation section. Adds Varies + Type to
// the chip vocabulary on top of the SOW shape (Status / Date range /
// Include archived). The Varies chip lists both the literal `Contract`
// token and one entry per non-cancelled, non-archived SOW so the user
// can scope to all variations affecting a particular engagement.

import { useEffect, useMemo, useRef, useState } from "react";
import type { Sow, VariationStatus } from "../../../../types";
import type {
  VariationDateRangeFilter,
  VariationFilters,
  VariationTypeFilter,
  VariesToken,
} from "./applyVariationFilters";
import DebouncedSearchInput from "../shared/DebouncedSearchInput";

export interface VariationFilterChipBarProps {
  filters: VariationFilters;
  onFiltersChange: (next: VariationFilters) => void;
  search: string;
  onSearchChange: (next: string) => void;
  /** SOWs available as Varies options. Cancelled / archived SOWs are
   *  filtered out at render time. Order is preserved from the parent. */
  sows: readonly Sow[];
  resetCounter?: number;
}

interface OptionDef<V extends string> {
  value: V;
  label: string;
}

const STATUS_OPTIONS: OptionDef<VariationStatus>[] = [
  { value: "proposed", label: "Proposed" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const TYPE_OPTIONS: OptionDef<VariationTypeFilter>[] = [
  { value: "financial", label: "Financial" },
  { value: "non-financial", label: "Non-financial" },
];

const DATE_RANGE_OPTIONS: OptionDef<VariationDateRangeFilter>[] = [
  { value: "next-30-days", label: "Next 30 days" },
  { value: "past-30-days", label: "Past 30 days" },
  { value: "this-quarter", label: "This quarter" },
  { value: "all-time", label: "All time" },
];

const CHIP_BASE =
  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-caption font-medium transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400";
const CHIP_INACTIVE =
  "border-slate-300 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700";
const CHIP_ACTIVE =
  "border-brand-400 bg-brand-50 text-brand-700 dark:border-brand-500 dark:bg-brand-950 dark:text-brand-300";
const POPOVER_CLASS =
  "absolute z-20 mt-1 min-w-[200px] max-h-[60vh] overflow-y-auto rounded-md border border-slate-200 bg-white p-2 shadow-soft-lg dark:shadow-soft-lg-dark dark:border-slate-700 dark:bg-slate-800";

interface MultiChipProps<V extends string> {
  chipKey: string;
  label: string;
  options: OptionDef<V>[];
  selected: V[];
  onToggle: (next: V[]) => void;
}

function MultiChip<V extends string>({
  chipKey,
  label,
  options,
  selected,
  onToggle,
}: MultiChipProps<V>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isActive = selected.length > 0;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  const toggle = (v: V) => {
    onToggle(
      selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v],
    );
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`${CHIP_BASE} ${isActive ? CHIP_ACTIVE : CHIP_INACTIVE}`}
        data-testid={`var-filter-chip-${chipKey}`}
      >
        <span>{label}</span>
        {isActive && (
          <span className="rounded bg-white px-1 text-[10px] text-brand-600 dark:bg-brand-900 dark:text-brand-300">
            {selected.length}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid={`var-filter-popover-${chipKey}`}
          role="listbox"
          aria-multiselectable="true"
          className={POPOVER_CLASS}
        >
          <ul className="space-y-1">
            {options.map((opt) => {
              const id = `var-filter-${chipKey}-${opt.value}`;
              return (
                <li key={opt.value}>
                  <label
                    htmlFor={id}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-body-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700"
                  >
                    <input
                      id={id}
                      type="checkbox"
                      checked={selected.includes(opt.value)}
                      onChange={() => toggle(opt.value)}
                      className="accent-brand-600"
                    />
                    <span>{opt.label}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

interface SingleChipProps<V extends string> {
  chipKey: string;
  label: string;
  options: OptionDef<V>[];
  value: V;
  defaultValue: V;
  onChange: (next: V) => void;
}

function SingleChip<V extends string>({
  chipKey,
  label,
  options,
  value,
  defaultValue,
  onChange,
}: SingleChipProps<V>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isActive = value !== defaultValue;
  const selectedLabel = options.find((o) => o.value === value)?.label ?? label;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`${CHIP_BASE} ${isActive ? CHIP_ACTIVE : CHIP_INACTIVE}`}
        data-testid={`var-filter-chip-${chipKey}`}
      >
        <span>{label}</span>
        {isActive && <span className="text-brand-700">· {selectedLabel}</span>}
      </button>
      {open && (
        <div
          data-testid={`var-filter-popover-${chipKey}`}
          role="listbox"
          className={POPOVER_CLASS}
        >
          <ul className="space-y-1">
            {options.map((opt) => {
              const id = `var-filter-${chipKey}-${opt.value}`;
              return (
                <li key={opt.value}>
                  <label
                    htmlFor={id}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-body-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700"
                  >
                    <input
                      id={id}
                      type="radio"
                      name={chipKey}
                      checked={value === opt.value}
                      onChange={() => {
                        onChange(opt.value);
                        setOpen(false);
                      }}
                      className="accent-brand-600"
                    />
                    <span>{opt.label}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function VariationFilterChipBar({
  filters,
  onFiltersChange,
  search,
  onSearchChange,
  sows,
  resetCounter,
}: VariationFilterChipBarProps) {
  const update = <K extends keyof VariationFilters>(
    key: K,
    value: VariationFilters[K],
  ) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const variesOptions = useMemo<OptionDef<VariesToken>[]>(() => {
    const opts: OptionDef<VariesToken>[] = [
      { value: "contract", label: "Contract" },
    ];
    for (const s of sows) {
      if (s.archivedAt) continue;
      if (s.status === "cancelled") continue;
      opts.push({ value: s.id, label: `${s.number} · ${s.name}` });
    }
    return opts;
  }, [sows]);

  return (
    <div
      className="flex flex-wrap items-center gap-2"
      data-testid="var-filter-chip-bar"
    >
      <DebouncedSearchInput
        value={search}
        onDebouncedChange={onSearchChange}
        resetCounter={resetCounter}
        ariaLabel="Search variations"
        placeholder="Search variations…"
        data-testid="var-filter-search"
      />
      <MultiChip
        chipKey="status"
        label="Status"
        options={STATUS_OPTIONS}
        selected={filters.status}
        onToggle={(next) => update("status", next)}
      />
      <MultiChip
        chipKey="varies"
        label="Varies"
        options={variesOptions}
        selected={filters.varies}
        onToggle={(next) => update("varies", next)}
      />
      <MultiChip
        chipKey="type"
        label="Type"
        options={TYPE_OPTIONS}
        selected={filters.type}
        onToggle={(next) => update("type", next)}
      />
      <SingleChip
        chipKey="date-range"
        label="Date range"
        options={DATE_RANGE_OPTIONS}
        value={filters.dateRange}
        defaultValue="all-time"
        onChange={(next) => update("dateRange", next)}
      />
      <button
        type="button"
        onClick={() => update("includeArchived", !filters.includeArchived)}
        aria-pressed={filters.includeArchived}
        className={`${CHIP_BASE} ${filters.includeArchived ? CHIP_ACTIVE : CHIP_INACTIVE}`}
        data-testid="var-filter-chip-archived"
      >
        Include archived
      </button>
    </div>
  );
}
